import { FC } from 'react';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Gamepad2, Coins, Calculator, Brain, ShoppingCart, PiggyBank, Lock, Star, Check } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { BaseGame } from './types';

// Obtener el icono del juego
const getGameIcon = (type: string) => {
  const icons = {
    'calculator': Calculator,
    'coins': Coins,
    'memory': Brain,
    'savings': PiggyBank,
    'budget': ShoppingCart,
    'business': Gamepad2
  };
  const Icon = icons[type as keyof typeof icons] || Gamepad2;
  return Icon;
};

// Obtener una descripción corta basada en el tipo de juego
const getShortDescription = (type: string): string => {
  const descriptions = {
    'calculator': '¡Matemáticas divertidas!',
    'coins': '¡Busca tesoros valiosos!',
    'memory': '¡Entrena tu memoria!',
    'savings': '¡Ahorra y crece!',
    'budget': '¡Planifica tus gastos!',
    'business': '¡Crea tu negocio!'
  };
  return descriptions[type as keyof typeof descriptions] || '¡Diviértete aprendiendo!';
};

// Obtener el título del juego basado en el tipo
const getGameTitle = (type: string, originalTitle: string): string => {
  // Si el tipo es 'coins', siempre mostrar "Cazador de Tesoros"
  if (type === 'coins') {
    return "Cazador de Tesoros";
  }
  // Si el tipo es 'memory', siempre mostrar "Memoria Financiera"
  if (type === 'memory') {
    return "Memoria Financiera";
  }
  // Para otros tipos, usar el título original
  return originalTitle;
};

interface GameCardProps {
  game: BaseGame;
  userSubscription?: string;
}

export const GameCard: FC<GameCardProps> = ({ game, userSubscription = 'free' }) => {
  const navigate = useNavigate();
  const isLocked = game.subscription_type === 'premium' && userSubscription !== 'premium';
  const GameIcon = getGameIcon(game.type);
  const shortDescription = getShortDescription(game.type);
  const displayTitle = getGameTitle(game.type, game.title);
  
  // Color base para juegos gratuitos (verde claro similar a "Nivel Básico")
  const baseColor = 'bg-gradient-to-br from-green-200 to-green-300';
  // Color para juegos premium (accent de la aplicación)
  const premiumColor = 'bg-gradient-to-br from-accent to-accent-dark';
  
  // Determinar el color a usar
  const cardColor = game.subscription_type === 'premium' ? premiumColor : baseColor;
  
  // Color para el fondo de los iconos (verde más fuerte)
  const iconBgColor = 'bg-green-500';
  
  // Color para el botón y la etiqueta "GRATIS" (verde más oscuro)
  const darkGreenColor = 'bg-green-700';
  
  const handlePlayGame = () => {
    if (isLocked) {
      toast({
        title: "Juego Premium",
        description: "Este juego está disponible solo para usuarios Premium. ¡Actualiza tu suscripción para acceder!",
        variant: "destructive"
      });
      return;
    }

    // Mapear tipos especiales a sus rutas correctas
    let gameRoute = game.type;
    
    // Corregir la ruta para el juego de Ahorro Inteligente
    if (game.type === 'savings_basic') {
      gameRoute = 'savings';
    }
    
    // Asegurarse de que el juego de Ahorro Inteligente use la ruta correcta
    if (game.title === 'Ahorro Inteligente' && gameRoute !== 'savings') {
      gameRoute = 'savings';
    }

    navigate(`/games/${gameRoute}`);
  };

  // Animación para el icono
  const iconAnimation = {
    hover: { 
      scale: 1.1,
      y: [0, -5, 0],
      transition: { 
        y: { repeat: Infinity, duration: 1.5, ease: "easeInOut" },
        scale: { duration: 0.2 }
      }
    }
  };

  // Animación para la tarjeta
  const cardAnimation = {
    hover: { 
      scale: 1.03,
      transition: { duration: 0.3 }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover="hover"
      variants={cardAnimation}
      className="cursor-pointer h-full"
      onClick={handlePlayGame}
    >
      <div className={`${cardColor} rounded-xl shadow-lg h-full overflow-hidden relative`}>
        {/* Etiqueta de tipo de suscripción */}
        <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-bold ${
          game.subscription_type === 'premium' 
            ? 'bg-orange text-white' 
            : `${darkGreenColor} text-white`
        }`}>
          {game.subscription_type === 'premium' ? 'PREMIUM' : 'GRATIS'}
        </div>
        
        <div className="p-5 flex flex-col items-center text-white h-full">
          {/* Parte superior con icono y título */}
          <div className="flex flex-col items-center text-center mb-4 flex-1">
            <div className={`p-4 ${iconBgColor} rounded-full mb-3`}>
              <motion.div variants={iconAnimation}>
                <GameIcon className="h-10 w-10" />
              </motion.div>
            </div>
            
            <h3 className="text-xl font-bold mb-1 text-green-900">{displayTitle}</h3>
            <p className="text-green-800 text-sm">{shortDescription}</p>
          </div>
          
          {/* Parte inferior con botón */}
          <div className="w-full mt-auto">
            {isLocked ? (
              <div className="bg-white/10 rounded-lg p-2 flex items-center justify-center gap-2">
                <Lock className="w-5 h-5" />
                <span className="font-medium">Requiere Premium</span>
              </div>
            ) : (
              <Button
                className={`w-full font-bold text-sm py-2 ${darkGreenColor} hover:bg-green-800 text-white`}
              >
                ¡JUGAR AHORA!
              </Button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
